/**
 * One-click keyless cloud connections for AWS and Microsoft Azure.
 *
 * Both work like the Google Cloud setup: the organization's GitHub Actions
 * sign in to the cloud with GitHub's OIDC token, and only from the
 * organization's own GitHub accounts. Nothing secret reaches CompBox.
 *
 * - AWS: a CloudFormation quick-create link. The admin clicks Create; the
 *   stack makes a deploy role (plus GitHub's OIDC provider if the account has
 *   none) and a small function tells CompBox the role it made.
 * - Azure: a "Deploy to Azure" template. The admin picks a resource group and
 *   clicks Create; the template makes a managed identity trusted by GitHub and,
 *   as its last step, fetches a link from CompBox that records what it made.
 */
import { isGitHubLogin } from "./cloud-setup.js";
// ---------------------------------------------------------------------------
// AWS
// ---------------------------------------------------------------------------
export const AWS_REGIONS = [
    "us-east-1",
    "us-east-2",
    "us-west-2",
    "ca-central-1",
    "eu-west-1",
    "eu-west-2",
    "eu-central-1",
    "ap-south-1",
    "ap-southeast-1",
    "ap-southeast-2",
    "ap-northeast-1",
];
const AWS_ACCOUNT = /^\d{12}$/;
const AWS_REGION = /^[a-z]{2}(?:-gov)?-[a-z]+-\d$/;
const AWS_ROLE_ARN = /^arn:aws[a-z-]*:iam::\d{12}:role\/[\w+=,.@/-]{1,512}$/;
const S3_BUCKET = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
export function isAwsAccountId(value) {
    return AWS_ACCOUNT.test(value);
}
export function isAwsRegion(value) {
    return AWS_REGION.test(value);
}
export function isAwsRoleArn(value) {
    return AWS_ROLE_ARN.test(value);
}
export function isS3BucketName(value) {
    return S3_BUCKET.test(value);
}
/** GitHub's OIDC certificate thumbprints (AWS no longer checks them for GitHub, but the API still accepts a list). */
const GITHUB_OIDC_THUMBPRINTS = ["6938fd4d98bab03faadb97b34396831e3780aea1", "1c58a3a8518e8759bf075b76b750d4f2df264fcd"];
/**
 * The Lambda behind the stack's two custom resources: "oidc" makes sure the
 * account trusts GitHub's OIDC issuer (an account can have only one, so an
 * existing one is reused), and "notify" tells CompBox what the stack made.
 */
const AWS_SETUP_FUNCTION = `import json, urllib.error, urllib.request
import boto3

ISSUER = "token.actions.githubusercontent.com"


def respond(event, ok, data=None, reason=""):
    body = json.dumps({
        "Status": "SUCCESS" if ok else "FAILED",
        "Reason": (reason or "See the function's CloudWatch logs")[:1000],
        "PhysicalResourceId": event.get("PhysicalResourceId") or event["LogicalResourceId"],
        "StackId": event["StackId"],
        "RequestId": event["RequestId"],
        "LogicalResourceId": event["LogicalResourceId"],
        "Data": data or {},
    }).encode()
    req = urllib.request.Request(event["ResponseURL"], data=body, method="PUT", headers={"Content-Type": ""})
    urllib.request.urlopen(req, timeout=30)


def github_oidc():
    iam = boto3.client("iam")
    for p in iam.list_open_id_connect_providers()["OpenIDConnectProviderList"]:
        arn = p["Arn"]
        if arn.endswith("/" + ISSUER):
            if "sts.amazonaws.com" not in iam.get_open_id_connect_provider(OpenIDConnectProviderArn=arn)["ClientIDList"]:
                iam.add_client_id_to_open_id_connect_provider(OpenIDConnectProviderArn=arn, ClientID="sts.amazonaws.com")
            return arn
    return iam.create_open_id_connect_provider(
        Url="https://" + ISSUER, ClientIDList=["sts.amazonaws.com"], ThumbprintList=${JSON.stringify(GITHUB_OIDC_THUMBPRINTS)}
    )["OpenIDConnectProviderArn"]


def detect(region):
    """Existing deploy pipelines (read-only, best effort; never fails the stack)."""
    found = {"codePipelines": [], "appRunnerServices": [], "ecsClusters": []}
    try:
        found["codePipelines"] = [x["name"] for x in boto3.client("codepipeline", region_name=region).list_pipelines()["pipelines"]][:25]
    except Exception as e:
        print("codepipeline", e)
    try:
        found["appRunnerServices"] = [x["ServiceName"] for x in boto3.client("apprunner", region_name=region).list_services()["ServiceSummaryList"]][:25]
    except Exception as e:
        print("apprunner", e)
    try:
        found["ecsClusters"] = [a.split("/")[-1] for a in boto3.client("ecs", region_name=region).list_clusters()["clusterArns"]][:25]
    except Exception as e:
        print("ecs", e)
    return found


def notify(p):
    body = json.dumps({
        "token": p["Token"], "accountId": p["AccountId"], "region": p["Region"],
        "roleArn": p["RoleArn"], "stateBucket": p["Bucket"], "detected": detect(p["Region"]),
    }).encode()
    req = urllib.request.Request(p["Url"], data=body, method="POST", headers={"Content-Type": "application/json"})
    try:
        urllib.request.urlopen(req, timeout=30)
    except urllib.error.HTTPError as e:
        raise RuntimeError("CompBox said: " + e.read().decode(errors="replace")[:500])


def handler(event, context):
    try:
        props = event["ResourceProperties"]
        if event["RequestType"] == "Delete":
            return respond(event, True)
        if props["Action"] == "oidc":
            return respond(event, True, {"Arn": github_oidc()})
        try:
            notify(props)
        except Exception:
            # The connect link expires; a later stack update shouldn't fail on it.
            if event["RequestType"] != "Update":
                raise
        respond(event, True)
    except Exception as e:
        print(e)
        respond(event, False, reason=str(e))
`;
/**
 * The CloudFormation template (JSON). It's the same for every organization —
 * the GitHub accounts, connect URL and connect token arrive as parameters —
 * so it can be published once to S3, which quick-create links require.
 */
export function buildAwsConnectTemplate() {
    const sub = (s) => ({ "Fn::Sub": s });
    return {
        AWSTemplateFormatVersion: "2010-09-09",
        Description: "CompBox keyless deploys: GitHub Actions from your GitHub accounts deploy as a role. No keys are stored.",
        Parameters: {
            GitHubOwners: { Type: "CommaDelimitedList", Description: "GitHub accounts whose Actions may deploy." },
            CompBoxConnectUrl: { Type: "String", Description: "Where the stack tells CompBox it's done." },
            CompBoxConnectToken: { Type: "String", NoEcho: true, Description: "One-time link from CompBox." },
        },
        Resources: {
            SetupFunctionRole: {
                Type: "AWS::IAM::Role",
                Properties: {
                    AssumeRolePolicyDocument: {
                        Version: "2012-10-17",
                        Statement: [{ Effect: "Allow", Principal: { Service: "lambda.amazonaws.com" }, Action: "sts:AssumeRole" }],
                    },
                    ManagedPolicyArns: [sub("arn:${AWS::Partition}:iam::aws:policy/service-role/AWSLambdaBasicExecutionRole")],
                    Policies: [
                        {
                            PolicyName: "github-oidc",
                            PolicyDocument: {
                                Version: "2012-10-17",
                                Statement: [
                                    {
                                        Effect: "Allow",
                                        Action: [
                                            "codepipeline:ListPipelines",
                                            "apprunner:ListServices",
                                            "ecs:ListClusters",
                                            "iam:ListOpenIDConnectProviders",
                                            "iam:GetOpenIDConnectProvider",
                                            "iam:CreateOpenIDConnectProvider",
                                            "iam:AddClientIDToOpenIDConnectProvider",
                                        ],
                                        Resource: "*",
                                    },
                                ],
                            },
                        },
                    ],
                },
            },
            SetupFunction: {
                Type: "AWS::Lambda::Function",
                Properties: {
                    Runtime: "python3.12",
                    Handler: "index.handler",
                    Timeout: 60,
                    Role: { "Fn::GetAtt": ["SetupFunctionRole", "Arn"] },
                    Code: { ZipFile: AWS_SETUP_FUNCTION },
                },
            },
            GitHubOidc: {
                Type: "Custom::GitHubOidc",
                Properties: { ServiceToken: { "Fn::GetAtt": ["SetupFunction", "Arn"] }, Action: "oidc" },
            },
            StateBucket: {
                Type: "AWS::S3::Bucket",
                DeletionPolicy: "Retain",
                UpdateReplacePolicy: "Retain",
                Properties: {
                    VersioningConfiguration: { Status: "Enabled" },
                    PublicAccessBlockConfiguration: {
                        BlockPublicAcls: true,
                        BlockPublicPolicy: true,
                        IgnorePublicAcls: true,
                        RestrictPublicBuckets: true,
                    },
                    BucketEncryption: { ServerSideEncryptionConfiguration: [{ ServerSideEncryptionByDefault: { SSEAlgorithm: "AES256" } }] },
                },
            },
            DeployRole: {
                Type: "AWS::IAM::Role",
                Properties: {
                    Description: "CompBox deploys (GitHub Actions, keyless)",
                    AssumeRolePolicyDocument: {
                        Version: "2012-10-17",
                        Statement: [
                            {
                                Effect: "Allow",
                                Principal: { Federated: { "Fn::GetAtt": ["GitHubOidc", "Arn"] } },
                                Action: "sts:AssumeRoleWithWebIdentity",
                                Condition: {
                                    StringEquals: { "token.actions.githubusercontent.com:aud": "sts.amazonaws.com" },
                                    // repo:<owner>/* for every owner.
                                    StringLike: {
                                        "token.actions.githubusercontent.com:sub": {
                                            "Fn::Split": [",", { "Fn::Sub": ["repo:${Owners}/*", { Owners: { "Fn::Join": ["/*,repo:", { Ref: "GitHubOwners" }] } }] }],
                                        },
                                    },
                                },
                            },
                        ],
                    },
                    ManagedPolicyArns: [
                        sub("arn:${AWS::Partition}:iam::aws:policy/AWSAppRunnerFullAccess"),
                        sub("arn:${AWS::Partition}:iam::aws:policy/AmazonEC2ContainerRegistryPowerUser"),
                    ],
                    Policies: [
                        {
                            PolicyName: "compbox-deploy",
                            PolicyDocument: {
                                Version: "2012-10-17",
                                Statement: [
                                    {
                                        Sid: "State",
                                        Effect: "Allow",
                                        Action: ["s3:ListBucket", "s3:GetObject", "s3:PutObject", "s3:DeleteObject"],
                                        Resource: [{ "Fn::GetAtt": ["StateBucket", "Arn"] }, sub("${StateBucket.Arn}/*")],
                                    },
                                    {
                                        Sid: "AppRoles",
                                        Effect: "Allow",
                                        Action: [
                                            "iam:GetRole",
                                            "iam:PassRole",
                                            "iam:CreateRole",
                                            "iam:DeleteRole",
                                            "iam:TagRole",
                                            "iam:AttachRolePolicy",
                                            "iam:DetachRolePolicy",
                                            "iam:PutRolePolicy",
                                            "iam:DeleteRolePolicy",
                                            "iam:ListAttachedRolePolicies",
                                            "iam:ListRolePolicies",
                                            "iam:GetRolePolicy",
                                        ],
                                        Resource: sub("arn:${AWS::Partition}:iam::${AWS::AccountId}:role/compbox-*"),
                                    },
                                    // ENG-451: month-to-date spend for the monthly cap (read-only).
                                    { Sid: "Spend", Effect: "Allow", Action: ["ce:GetCostAndUsage"], Resource: "*" },
                                ],
                            },
                        },
                    ],
                },
            },
            Connect: {
                Type: "Custom::CompBoxConnect",
                Properties: {
                    ServiceToken: { "Fn::GetAtt": ["SetupFunction", "Arn"] },
                    Action: "notify",
                    Url: { Ref: "CompBoxConnectUrl" },
                    Token: { Ref: "CompBoxConnectToken" },
                    AccountId: { Ref: "AWS::AccountId" },
                    Region: { Ref: "AWS::Region" },
                    RoleArn: { "Fn::GetAtt": ["DeployRole", "Arn"] },
                    Bucket: { Ref: "StateBucket" },
                    // Re-tell CompBox when the GitHub accounts change.
                    Owners: { "Fn::Join": [",", { Ref: "GitHubOwners" }] },
                },
            },
        },
        Outputs: {
            DeployRoleArn: { Value: { "Fn::GetAtt": ["DeployRole", "Arn"] } },
            StateBucket: { Value: { Ref: "StateBucket" } },
        },
    };
}
/** The AWS console link that opens the stack with everything filled in. */
export function awsQuickCreateUrl(input) {
    if (!isAwsRegion(input.region))
        throw new Error("Not an AWS region.");
    const owners = uniqueOwners(input.githubOwners);
    const params = new URLSearchParams({
        templateURL: input.templateUrl,
        stackName: "CompBox",
        param_GitHubOwners: owners.join(","),
        param_CompBoxConnectUrl: input.connectUrl,
        param_CompBoxConnectToken: input.connectToken,
    });
    return `https://${input.region}.console.aws.amazon.com/cloudformation/home?region=${input.region}#/stacks/quickcreate?${params.toString()}`;
}
// ---------------------------------------------------------------------------
// Azure
// ---------------------------------------------------------------------------
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RESOURCE_GROUP = /^[-\w.()]{0,89}[-\w()]$/;
const AZURE_LOCATION = /^[a-z0-9]{2,40}$/;
const STORAGE_ACCOUNT = /^[a-z0-9]{3,24}$/;
export function isAzureGuid(value) {
    return GUID.test(value);
}
export function isAzureResourceGroup(value) {
    return RESOURCE_GROUP.test(value);
}
export function isAzureLocation(value) {
    return AZURE_LOCATION.test(value);
}
export function isAzureStorageAccount(value) {
    return STORAGE_ACCOUNT.test(value);
}
export const AZURE_DEPLOY_IDENTITY = "compbox-deploy";
/** Built-in role IDs. */
const AZURE_CONTRIBUTOR = "b24988ac-6180-42a0-ab88-20f7382dd24c";
const AZURE_BLOB_DATA_CONTRIBUTOR = "ba92f5b4-2d11-453d-a403-e96b0029c9fe";
/**
 * GitHub's OIDC subject for a repository whose subject template is set to
 * `repository_owner` — the only form Azure's exact-match federated
 * credentials can trust for a whole GitHub account.
 */
export function azureGitHubSubject(owner) {
    return `repository_owner:${owner}`;
}
/**
 * The ARM template behind the "Deploy to Azure" button, for one organization.
 * Deployed into a resource group: a managed identity trusted by the org's
 * GitHub accounts, Contributor on that group, a storage account for
 * infrastructure state, and — last — a nested deployment whose template link
 * is CompBox's `connectUrl` (which already has a query string) plus what was
 * made. Fetching
 * that link is how CompBox learns the deployment finished.
 */
export function buildAzureConnectTemplate(input) {
    const owners = uniqueOwners(input.githubOwners);
    const identityId = `[resourceId('Microsoft.ManagedIdentity/userAssignedIdentities', '${AZURE_DEPLOY_IDENTITY}')]`;
    const identityRef = `reference(resourceId('Microsoft.ManagedIdentity/userAssignedIdentities', '${AZURE_DEPLOY_IDENTITY}'), '2023-01-31')`;
    return {
        $schema: "https://schema.management.azure.com/schemas/2019-04-01/deploymentTemplate.json#",
        contentVersion: "1.0.0.0",
        metadata: { description: "CompBox keyless deploys: GitHub Actions from your GitHub accounts deploy as a managed identity." },
        parameters: {
            location: { type: "string", defaultValue: "[resourceGroup().location]" },
            githubOwners: { type: "array", defaultValue: owners },
            connectUrl: { type: "string", defaultValue: input.connectUrl },
        },
        variables: {
            storageName: "[concat('cbstate', uniqueString(resourceGroup().id))]",
        },
        resources: [
            {
                type: "Microsoft.ManagedIdentity/userAssignedIdentities",
                apiVersion: "2023-01-31",
                name: AZURE_DEPLOY_IDENTITY,
                location: "[parameters('location')]",
            },
            {
                type: "Microsoft.ManagedIdentity/userAssignedIdentities/federatedIdentityCredentials",
                apiVersion: "2023-01-31",
                name: `[concat('${AZURE_DEPLOY_IDENTITY}/github-', parameters('githubOwners')[copyIndex()])]`,
                // Azure rejects concurrent writes to one identity's credentials.
                copy: { name: "owners", count: "[length(parameters('githubOwners'))]", mode: "Serial", batchSize: 1 },
                dependsOn: [identityId],
                properties: {
                    issuer: "https://token.actions.githubusercontent.com",
                    subject: "[concat('repository_owner:', parameters('githubOwners')[copyIndex()])]",
                    audiences: ["api://AzureADTokenExchange"],
                },
            },
            {
                type: "Microsoft.Authorization/roleAssignments",
                apiVersion: "2022-04-01",
                name: `[guid(resourceGroup().id, '${AZURE_DEPLOY_IDENTITY}', 'contributor')]`,
                dependsOn: [identityId],
                properties: {
                    roleDefinitionId: `[subscriptionResourceId('Microsoft.Authorization/roleDefinitions', '${AZURE_CONTRIBUTOR}')]`,
                    principalId: `[${identityRef}.principalId]`,
                    principalType: "ServicePrincipal",
                },
            },
            {
                type: "Microsoft.Storage/storageAccounts",
                apiVersion: "2023-01-01",
                name: "[variables('storageName')]",
                location: "[parameters('location')]",
                sku: { name: "Standard_LRS" },
                kind: "StorageV2",
                properties: { minimumTlsVersion: "TLS1_2", allowBlobPublicAccess: false, supportsHttpsTrafficOnly: true },
            },
            {
                type: "Microsoft.Storage/storageAccounts/blobServices/containers",
                apiVersion: "2023-01-01",
                name: "[concat(variables('storageName'), '/default/tfstate')]",
                dependsOn: ["[resourceId('Microsoft.Storage/storageAccounts', variables('storageName'))]"],
            },
            {
                type: "Microsoft.Authorization/roleAssignments",
                apiVersion: "2022-04-01",
                name: `[guid(resourceGroup().id, '${AZURE_DEPLOY_IDENTITY}', 'state')]`,
                scope: "[concat('Microsoft.Storage/storageAccounts/', variables('storageName'))]",
                dependsOn: [identityId, "[resourceId('Microsoft.Storage/storageAccounts', variables('storageName'))]"],
                properties: {
                    roleDefinitionId: `[subscriptionResourceId('Microsoft.Authorization/roleDefinitions', '${AZURE_BLOB_DATA_CONTRIBUTOR}')]`,
                    principalId: `[${identityRef}.principalId]`,
                    principalType: "ServicePrincipal",
                },
            },
            {
                type: "Microsoft.Resources/deployments",
                apiVersion: "2022-09-01",
                name: "compbox-connect",
                dependsOn: [
                    "owners",
                    `[resourceId('Microsoft.Authorization/roleAssignments', guid(resourceGroup().id, '${AZURE_DEPLOY_IDENTITY}', 'contributor'))]`,
                    "[resourceId('Microsoft.Storage/storageAccounts/blobServices/containers', variables('storageName'), 'default', 'tfstate')]",
                ],
                properties: {
                    mode: "Incremental",
                    templateLink: {
                        uri: "[format('{0}&subscriptionId={1}&tenantId={2}&resourceGroup={3}&location={4}&clientId={5}&storageAccount={6}', " +
                            "parameters('connectUrl'), subscription().subscriptionId, subscription().tenantId, uriComponent(resourceGroup().name), " +
                            `uriComponent(parameters('location')), ${identityRef}.clientId, variables('storageName'))]`,
                    },
                },
            },
        ],
        outputs: {
            clientId: { type: "string", value: `[${identityRef}.clientId]` },
            storageAccount: { type: "string", value: "[variables('storageName')]" },
        },
    };
}
/** The empty template CompBox answers the connect link with. */
export const EMPTY_ARM_TEMPLATE = {
    $schema: "https://schema.management.azure.com/schemas/2019-04-01/deploymentTemplate.json#",
    contentVersion: "1.0.0.0",
    resources: [],
};
/** The portal link behind the "Deploy to Azure" button. */
export function azureDeployUrl(templateUrl) {
    return `https://portal.azure.com/#create/Microsoft.Template/uri/${encodeURIComponent(templateUrl)}`;
}
function uniqueOwners(owners) {
    if (owners.length === 0)
        throw new Error("Connect GitHub first.");
    for (const owner of owners) {
        if (!isGitHubLogin(owner))
            throw new Error(`Not a GitHub account name: ${owner}`);
    }
    return [...new Set(owners)];
}
//# sourceMappingURL=cloud-connect.js.map